import React, { useState } from 'react';
import { 
  Bell, 
  AlertTriangle, 
  CheckCircle2, 
  ShieldAlert, 
  Filter, 
  PlusCircle, 
  Calendar, 
  Layers, 
  ArrowRight, 
  Check, 
  Sparkles,
  Info
} from 'lucide-react';
import { SeverityBadge } from '../components/common/StatusIndicator';
import { useStore } from '../store/useStore';
import { SeverityLevel } from '../types';

export const AlertsPage: React.FC = () => {
  const { alerts, resolveAlert, openAiModal } = useStore();
  const [filterTab, setFilterTab] = useState<'all' | 'active' | 'resolved'>('all');
  const [severityFilter, setSeverityFilter] = useState<'ALL' | SeverityLevel>('ALL');
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const filteredAlerts = alerts.filter(item => {
    if (filterTab === 'active' && item.isResolved) return false;
    if (filterTab === 'resolved' && !item.isResolved) return false;
    if (severityFilter !== 'ALL' && item.severity !== severityFilter) return false;
    return true;
  });

  const handleResolve = async (id: string) => {
    setResolvingId(id);
    await resolveAlert(id);
    setResolvingId(null);
  };

  const activeCount = alerts.filter(a => !a.isResolved).length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white">Identity & Threat Alerts</h1>
            {activeCount > 0 ? (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-800">
                {activeCount} Active
              </span>
            ) : (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-800">
                All Cleared
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Real-time notifications regarding corporate breaches, leaked documents, credential exposure, and scam alerts.
          </p>
        </div>

        <button
          onClick={openAiModal}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-xs font-semibold text-emerald-400 transition-colors self-start sm:self-auto"
        >
          <Sparkles className="w-4 h-4" />
          <span>Ask AI What To Do</span>
        </button>
      </div>

      {/* Severity Guide Pill */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
          <div className="font-bold text-slate-300">LOW</div>
          <div className="text-[11px] text-slate-500">Informational advice</div>
        </div>
        <div className="p-2.5 rounded-xl bg-amber-950/40 border border-amber-800/50">
          <div className="font-bold text-amber-400">MEDIUM</div>
          <div className="text-[11px] text-slate-400">Requires prompt attention</div>
        </div>
        <div className="p-2.5 rounded-xl bg-orange-950/40 border border-orange-800/50">
          <div className="font-bold text-orange-400">HIGH</div>
          <div className="text-[11px] text-slate-400">Potential fraud attempt</div>
        </div>
        <div className="p-2.5 rounded-xl bg-rose-950/40 border border-rose-800/50">
          <div className="font-bold text-rose-400">CRITICAL</div>
          <div className="text-[11px] text-slate-400">Possible active ID theft</div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
        {/* Status tabs */}
        <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => setFilterTab('all')}
            className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
              filterTab === 'all' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            All ({alerts.length})
          </button>
          <button
            onClick={() => setFilterTab('active')}
            className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
              filterTab === 'active' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            Active ({activeCount})
          </button>
          <button
            onClick={() => setFilterTab('resolved')}
            className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
              filterTab === 'resolved' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            Resolved ({alerts.length - activeCount})
          </button>
        </div>

        {/* Severity select */}
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-400">Severity:</span>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as any)}
            className="px-2.5 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-white text-xs focus:outline-none"
          >
            <option value="ALL">All Severities</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </select>
        </div>
      </div>

      {/* Alerts Stream */}
      <div className="space-y-4">
        {filteredAlerts.length === 0 ? (
          <div className="p-12 text-center bg-slate-900 border border-slate-800 rounded-3xl space-y-3">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h3 className="text-base font-bold text-white">No alerts found in this view</h3>
            <p className="text-xs text-slate-400">Your monitored Australian accounts and credentials have no pending items here.</p>
          </div>
        ) : (
          filteredAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-6 rounded-2xl border transition-all shadow-lg space-y-4 ${
                alert.isResolved
                  ? 'bg-slate-900/60 border-slate-800/80 opacity-75'
                  : alert.severity === 'CRITICAL'
                    ? 'bg-rose-950/20 border-rose-800/70'
                    : alert.severity === 'HIGH'
                      ? 'bg-orange-950/20 border-orange-800/70'
                      : alert.severity === 'MEDIUM'
                        ? 'bg-amber-950/20 border-amber-800/70'
                        : 'bg-slate-900 border-slate-800'
              }`}
            >
              {/* Alert Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
                <div className="flex flex-wrap items-center gap-2.5">
                  <SeverityBadge severity={alert.severity} />
                  <span className="text-xs font-mono text-slate-400">
                    {new Date(alert.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {alert.organisation && (
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-medium">
                      {alert.organisation}
                    </span>
                  )}
                </div>

                {alert.isResolved ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400">
                    <CheckCircle2 className="w-4 h-4" />
                    Resolved
                  </span>
                ) : (
                  <button
                    onClick={() => handleResolve(alert.id)}
                    disabled={resolvingId === alert.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-800 hover:bg-emerald-600 text-slate-200 hover:text-white text-xs font-semibold transition-colors border border-slate-700 self-start sm:self-auto"
                  >
                    {resolvingId === alert.id ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    <span>I've resolved this</span>
                  </button>
                )}
              </div>

              {/* Title & Description */}
              <div className="space-y-1">
                <h3 className="text-base font-bold text-white">{alert.title}</h3>
                <p className="text-xs text-slate-300 leading-relaxed">{alert.whatHappened}</p>
              </div>

              {/* Information Exposed Tag Cloud */}
              {alert.exposedInformation && alert.exposedInformation.length > 0 && alert.exposedInformation[0] !== 'None' && (
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Information Potentially Exposed:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {alert.exposedInformation.map((info, idx) => (
                      <span key={idx} className="px-2.5 py-0.5 rounded-md bg-slate-950 border border-slate-800 text-slate-300 text-[11px]">
                        {info}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Action Box */}
              <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800/80 flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-slate-200">
                  <span className="font-semibold text-emerald-400">Recommended Action: </span>
                  {alert.recommendedAction}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
